/**
 * PDF document navigation with single-page or continuous scrolling.
 * PDFPage owns stable geometry, viewport visibility and bounded raster buffers.
 * Selection coordinates remain CSS viewport coordinates, independent of DPR.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Document, pdfjs } from "react-pdf";
import PDFPageWrapper from "./PDFPage";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url,
).toString();

/* ── Single-page wrapper (internal, memoised) ───────────────── */



/* ── Document-level component (public) ────────────────────────── */

export default function PDFViewer({
  file,
  pageWidth = 800,
  fitHeight = 900,
  fitMode = "page",
  zoom = 1,
  continuous = true,
  currentPage = 1,
  onDocumentLoad,
  onPageRender,
  renderPageOverlay,
}) {
  const [numPages, setNumPages] = useState(0);
  const [fileUrl, setFileUrl] = useState(null);

  // Convert File blob to an object URL for react-pdf
  useEffect(() => {
    if (!file) { setFileUrl(null); return; }
    const url = typeof file === "string" ? file : URL.createObjectURL(file);
    setFileUrl(url);
    return () => { if (typeof file !== "string") URL.revokeObjectURL(url); };
  }, [file]);

  const onDocumentLoadSuccess = useCallback(
    (pdf) => {
      setNumPages(pdf.numPages);
      onDocumentLoad?.(pdf.numPages);
    },
    [onDocumentLoad],
  );

  if (!fileUrl) return null;

  return (
    <Document
      file={fileUrl}
      onLoadSuccess={onDocumentLoadSuccess}
      loading={
        <div className="pdf-loading">
          <span className="spinner" />
          Loading document…
        </div>
      }
      error={<div className="pdf-error">Failed to load PDF document.</div>}
    >
      <div className="pdf-pages">
        {Array.from({ length: numPages }, (_, i) => i + 1).filter(number => continuous || number === currentPage).map(number => (
          <PDFPageWrapper
            key={number}
            pageNumber={number}
            pageWidth={pageWidth}
            fitHeight={fitHeight}
            fitMode={fitMode}
            zoom={zoom}
            onRender={onPageRender}
            renderOverlay={renderPageOverlay}
          />
        ))}
      </div>
    </Document>
  );
}
